import type { TrackDeviceIdCookie, TrackingInfoCookie } from "@reco/service/src/types/cookies";
import type {
  CriteoPlacementData,
  CriteoProduct,
  CriteoResponseItem,
  PreparedProduct,
  UserContext,
} from "@reco/service/src/types/sponsored";
import type { RecommendationsData } from "@reco/shared/src/types/recommendation";

import { fetchWithTimeout } from "@reco/service/src/utils/api";
import { getEnvironmentConfig } from "@reco/service/src/utils/config";
import { toKebabParams } from "@reco/service/src/utils/mappers";
import { parseBase64Json, parseKeyValueString } from "@reco/service/src/utils/parsers";
import { getCookieValue } from "@reco/service/src/utils/storage";
import { getRuntimeConfig } from "@reco/shared/src/runtime-config";
import { EMAIL_REGEX } from "./constants";

const parsePartnerIds = (envVarValue: string | undefined, varName: string): Record<string, string> => {
  try {
    // ODJ doesn't allow double quotes, so we replace single quotes with double quotes to parse the JSON string correctly
    const validJsonStr = envVarValue?.replaceAll("'", '"') || "{}";
    return JSON.parse(validJsonStr);
  } catch (error) {
    console.error(`[CriteoAPIService] Failed to parse build-time ${varName} JSON.`, error);
    return {};
  }
};

const CRITEO_PARTNER_IDS_QA_MAP = parsePartnerIds(
  import.meta.env.ODJ_DEP_RECO_FRONTEND_VARS_PUBLIC_CRITEO_PARTNER_IDS_QA,
  "ODJ_DEP_RECO_FRONTEND_VARS_PUBLIC_CRITEO_PARTNER_IDS_QA",
);
const CRITEO_PARTNER_IDS_PROD_MAP = parsePartnerIds(
  import.meta.env.ODJ_DEP_RECO_FRONTEND_VARS_PUBLIC_CRITEO_PARTNER_IDS_PROD,
  "ODJ_DEP_RECO_FRONTEND_VARS_PUBLIC_CRITEO_PARTNER_IDS_PROD",
);

export default class CriteoAPIService {
  private static readonly ENDPOINT = "/t/cri/delivery/retailmedia";
  private static readonly EMPTY_RESPONSE = { recommendationItems: [] };
  private static readonly TRACKING_INFO_COOKIE = "tracking-info";
  private static readonly LIDL_ID_COOKIE = "LidlID";
  private static readonly TRACK_DEVICE_ID_COOKIE = "track_device_id";
  private static readonly SALES_CHANNEL_ANDROID = "06";
  private static readonly SALES_CHANNEL_IOS = "07";

  public static async get(productId?: string, countryId?: string): Promise<RecommendationsData> {
    const partnerId = this.getPartnerIdLegacy(countryId) || this.getPartnerId(countryId);
    const userContext = this.getUserContext();

    // NEOSRF-664: In old cases where emails are still stored in plain text, we want to ignore them and not send them to Criteo to avoid sending PII
    if (userContext?.email && EMAIL_REGEX.test(userContext.email)) {
      userContext.email = undefined;
    }

    const missingParams: string[] = [];
    if (!partnerId) missingParams.push("CRITEO Partner Id");
    if (!productId) missingParams.push("Product Id");
    if (!userContext?.retailVisitorId) missingParams.push("Retail Visitor Id");

    if (missingParams.length > 0) {
      console.warn(
        `[RECO Sponsored Slider] Aborting Criteo request. Missing required parameters: ${missingParams.join(", ")}`,
      );
      return CriteoAPIService.EMPTY_RESPONSE;
    }

    try {
      const reqParams = await this.buildRequestParams(partnerId!, productId!, userContext!);

      const fetchResponse = await fetchWithTimeout(
        toKebabParams(this.ENDPOINT, reqParams),
        {
          mode: "cors",
          method: "GET",
        },
        500,
      );

      const response: CriteoResponseItem = await fetchResponse.json();
      const products = this.processPlacements(response.placements);

      if (products.length === 0) {
        return this.EMPTY_RESPONSE;
      }

      return {
        recommendationItems: products,
      };
    } catch (error) {
      console.error("[CriteoAPIService] Failed to fetch placements for current product.", error);
      return CriteoAPIService.EMPTY_RESPONSE;
    }
  }

  private static async buildRequestParams(
    partnerId: string,
    productId: string,
    userContext: UserContext,
  ): Promise<Record<string, string | undefined>> {
    const productData = globalThis.unified_datalayer_product;
    const availability = productData?.availability === "available" ? "1" : "0";

    return {
      criteoPartnerId: partnerId,
      retailerVisitorId: userContext.retailVisitorId,
      customerId: userContext.customerId,
      email: userContext.email,
      eventType: "viewItem",
      pageId: this.getPageID(),
      item: productId,
      price: productData?.price?.toString(),
      availability: availability,
    };
  }

  private static getUserContext(): UserContext | null {
    const trackDeviceIDCookie = getCookieValue(this.TRACK_DEVICE_ID_COOKIE);

    if (trackDeviceIDCookie) {
      try {
        const trackDevice = parseKeyValueString<TrackDeviceIdCookie>(trackDeviceIDCookie);
        const emailHash = trackDevice.EMAIL_HASH?.trim();

        return {
          retailVisitorId: trackDevice.DEVICE_ID,
          customerId: trackDevice.SSO_ID,
          email: emailHash,
        };
      } catch (error) {
        console.error("[CriteoAPIService] Failed to parse track_device_id cookie.", error);
        return null;
      }
    }

    const lidlID = getCookieValue(this.LIDL_ID_COOKIE);
    const trackingInfoCookieEncoded = getCookieValue(this.TRACKING_INFO_COOKIE);
    const trackingInfo = parseBase64Json<TrackingInfoCookie>(trackingInfoCookieEncoded);
    const cleanedEmailHash = trackingInfo?.email.toLocaleLowerCase().trim();

    return {
      retailVisitorId: lidlID,
      customerId: trackingInfo?.ssoId,
      email: cleanedEmailHash,
    };
  }

  private static getPartnerIdLegacy(countryId?: string): string | undefined {
    if (!countryId) return undefined;
    const normalizedCountryId = countryId.toLowerCase();
    const { env } = getEnvironmentConfig();

    switch (env) {
      case "qa":
        return CRITEO_PARTNER_IDS_QA_MAP[normalizedCountryId];
      case "prod":
        return CRITEO_PARTNER_IDS_PROD_MAP[normalizedCountryId];
      default:
        return undefined;
    }
  }

  private static cachedPartnerIds: Record<string, string> | null = null;

  private static getPartnerId(countryId?: string): string | undefined {
    if (!countryId) return undefined;

    if (!this.cachedPartnerIds) {
      try {
        const configStr = getRuntimeConfig().criteoPartnerIds?.replaceAll("'", '"') || "{}";
        const rawConfig = JSON.parse(configStr);

        this.cachedPartnerIds = Object.fromEntries(
          Object.entries(rawConfig).map(([key, value]) => [key.toUpperCase(), String(value)]),
        );
      } catch (error) {
        this.cachedPartnerIds = {};
        console.warn("Failed to parse criteoPartnerIds config", error);
      }
    }

    return this.cachedPartnerIds[countryId];
  }

  private static getPageID(): string {
    let device = "";
    const trackDeviceIDCookie = getCookieValue(this.TRACK_DEVICE_ID_COOKIE);

    if (trackDeviceIDCookie) {
      try {
        const trackDevice = parseKeyValueString<TrackDeviceIdCookie>(trackDeviceIDCookie);
        if (trackDevice?.SALES_CHANNEL === this.SALES_CHANNEL_ANDROID) {
          device = "Aa";
        } else if (trackDevice?.SALES_CHANNEL === this.SALES_CHANNEL_IOS) {
          device = "Aios";
        }
      } catch (error) {
        console.error("Failed to parse track_device_id cookie", error);
      }
    }

    if (!device) {
      const mql = globalThis.matchMedia?.("(max-width: 749px)");
      device = mql?.matches ? "Mobile" : "Desktop";
    }

    return `viewItemApi${device}`;
  }

  private static processPlacements(placements: CriteoResponseItem["placements"]): PreparedProduct[] {
    return placements.reduce((accumulator, placementObject) => {
      const placementKey = Object.keys(placementObject)[0];
      if (!placementKey) {
        return accumulator;
      }

      const placementDataArray = placementObject[placementKey];
      if (!placementDataArray || placementDataArray.length === 0) {
        return accumulator;
      }

      const placementDetails = placementDataArray[0];
      const mappedProducts = placementDetails.products.map((productItem) =>
        this.transformProduct(productItem, placementDetails),
      );

      return [...accumulator, ...mappedProducts];
    }, [] as PreparedProduct[]);
  }

  private static transformProduct(item: CriteoProduct, placementDetails: CriteoPlacementData): PreparedProduct {
    return {
      itemId: item.ProductId,
      relevance: 1,
      beacons: {
        promoted: true,
        ad: JSON.stringify(item),
        adPlacementLoadBeacon: placementDetails.OnLoadBeacon,
        adPlacementViewBeacon: placementDetails.OnViewBeacon,
        adLoadBeacon: item.OnLoadBeacon,
        adViewBeacon: item.OnViewBeacon,
        adClickBeacon: item.OnClickBeacon,
        adBasketChangeBeacon: item.OnBasketChangeBeacon,
        adWishlistBeacon: item.OnWishlistBeacon,
      },
    };
  }
}
